import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * La mascota que espera: sólo el contorno del cuerpo, girando, y sus ojos.
 *
 * Vive aquí y no en el panel porque las dos aplicaciones esperan por lo mismo y
 * tienen que decirlo igual. El editor arranca con «Verificando sesión» y
 * «Cargando proyecto», que son la misma clase de momento que abrir una lista en
 * el panel; con dos indicadores distintos, cruzar de una aplicación a la otra se
 * lee como cambiar de producto.
 *
 * Sólo el contorno, sin el relleno azul del personaje: un indicador se mira de
 * reojo y durante segundos, y la figura sólida a este tamaño pesa como un objeto
 * más de la pantalla.
 *
 * ── La secuencia ────────────────────────────────────────────────────────────
 * Gira con respiración: cada ciclo arranca lento, se lanza a media vuelta y
 * vuelve a calmarse, una y otra vez mientras se espera. Al terminar no frena ni
 * guiña —eso eran casi cuatro décimas de despedida que había que aguantar antes
 * de ver el contenido—: sigue girando y se desvanece.
 */
import { useEffect, useRef, useState } from 'react';
/** El contorno del cuerpo, en reposo. Seis lados, esquinas suavizadas. */
const BODY = 'M106.75 45.71 C111.31 53.60 111.31 66.40 106.75 74.29 L95.75 93.34 C91.19 101.23 80.11 107.63 71.00 107.63 ' +
    'L49.00 107.63 C39.89 107.63 28.81 101.23 24.25 93.34 L13.25 74.29 C8.69 66.40 8.69 53.60 13.25 45.71 ' +
    'L24.25 26.66 C28.81 18.77 39.89 12.37 49.00 12.37 L71.00 12.37 C80.11 12.37 91.19 18.77 95.75 26.66 Z';
/**
 * El ciclo: dos vueltas enteras, lento-rápido-lento.
 *
 * Dos y no una porque el tramo rápido tiene que llegar a leerse como carrera; en
 * una sola vuelta, la parte veloz dura un suspiro y lo que queda es un balanceo.
 * Y como el ciclo empieza y acaba en la misma fase lenta y en la misma
 * orientación, encadenarlo infinitas veces no deja costura: el arranque desde
 * parado ya es el principio del propio ciclo, sin rampa aparte.
 */
const CYCLE_MS = 1700;
const CYCLE_DEG = 720;
/**
 * La curva de la respiración.
 *
 * Los extremos no llegan a cero —la pendiente en los bordes es una novena parte
 * de la media—, así que en el punto más lento el contorno sigue avanzando. Con
 * una curva que se para del todo, cada ciclo parece un cuelgue de la pantalla en
 * vez de un gesto.
 */
const CYCLE_EASING = 'cubic-bezier(0.55, 0.06, 0.45, 0.94)';
/** Lo que tarda en irse cuando ya hay datos. */
const FADE_MS = 180;
/**
 * El aterrizaje del modo insignia: lo que tarda en posarse derecha.
 *
 * Sólo lo usa `restWhenIdle`. El cuerpo es un hexágono simétrico a media vuelta,
 * así que «derecha» es cualquier múltiplo de 180 grados: desde donde esté, media
 * vuelta como mucho.
 */
const LAND_MS = 520;
/** Ids únicos para el degradado del modo relleno: dos mascotas en pantalla no comparten. */
let filledInstances = 0;
export function LoadingMascot({ spinning, size, restWhenIdle = false, filled = false }) {
    const ringRef = useRef(null);
    const [fillId] = useState(() => `vl-mascot-fill-${(filledInstances = (filledInstances + 1) % 1e6)}`);
    const spinRef = useRef(null);
    /* Quieta de verdad: ni gira ni se va. La despedida normal SÍ gira mientras se
       desvanece, y por eso no entra aquí. */
    const still = restWhenIdle && !spinning;
    /* El giro va por la API de animaciones y no por CSS por dos cosas: la mascota
       se va girando —con `animation` en la clase que la lleva y otra que la saca
       habría dos peleándose por el elemento— y el modo insignia necesita
       preguntarle por dónde va para posarse desde ahí.
  
       `still` es la única dependencia: con `spinning` aquí, el giro se reiniciaría
       desde cero justo al empezar el desvanecido, que es un salto a plena
       velocidad en el fotograma más visible. */
    useEffect(() => {
        const ring = ringRef.current;
        if (!ring || still)
            return;
        const turn = ring.animate([{ transform: 'rotate(0deg)' }, { transform: `rotate(${CYCLE_DEG}deg)` }], { duration: CYCLE_MS, easing: CYCLE_EASING, iterations: Infinity });
        spinRef.current = turn;
        /* PAUSAR, no cancelar: cancelar retira el efecto y devuelve el contorno a
           cero en el acto, y esta limpieza corre ANTES del efecto que lee el ángulo
           para posarse. Lo que leería sería siempre cero. */
        return () => { turn.pause(); };
    }, [still]);
    /* Posarse derecha, sólo en el modo insignia. */
    useEffect(() => {
        const ring = ringRef.current;
        if (!ring || !still)
            return;
        let from = 0;
        try {
            const matrix = new DOMMatrixReadOnly(getComputedStyle(ring).transform);
            from = (Math.atan2(matrix.b, matrix.a) * 180) / Math.PI;
        }
        catch {
            /* Sin matriz que leer se posa desde cero: como mucho, media vuelta de
               salto en una figura que a media vuelta es la misma. */
        }
        spinRef.current?.cancel();
        spinRef.current = null;
        const land = ring.animate([{ transform: `rotate(${from}deg)` }, { transform: `rotate(${from + (((180 - from) % 180) + 180) % 180}deg)` }], { duration: LAND_MS, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' });
        return () => land.cancel();
    }, [still]);
    /* Al desmontar, lo que quedara pausado se cancela: sin esto un giro sobrevive
       al nodo si la pantalla se cierra a mitad de la espera. */
    useEffect(() => () => {
        spinRef.current?.cancel();
        spinRef.current = null;
    }, []);
    return (_jsxs("span", { className: `vl-mascot${filled ? ' is-filled' : ''}${spinning || restWhenIdle ? '' : ' is-done'}`, style: size ? { '--vl-mascot-size': `${size}px` } : undefined, "aria-hidden": "true", children: [_jsx("svg", { className: "vl-mascot-shape", viewBox: "0 0 120 120", children: filled ? (_jsxs(_Fragment, { children: [_jsxs("defs", { children: [_jsxs("linearGradient", { id: `${fillId}-g`, x1: "60", y1: "0", x2: "60", y2: "120", gradientUnits: "userSpaceOnUse", children: [_jsx("stop", { stopColor: "var(--vl-accent-sky)" }), _jsx("stop", { offset: "0.42", stopColor: "var(--vl-accent-blue)" }), _jsx("stop", { offset: "1", stopColor: "var(--vl-accent-deep)" })] }), _jsx("clipPath", { id: `${fillId}-clip`, children: _jsx("path", { ref: ringRef, className: "vl-mascot-ring", d: BODY }) })] }), _jsx("rect", { x: "-10", y: "-10", width: "140", height: "140", fill: `url(#${fillId}-g)`, clipPath: `url(#${fillId}-clip)` })] })) : (_jsx("path", { ref: ringRef, className: "vl-mascot-ring", d: BODY })) }), _jsxs("span", { className: "vl-mascot-blink", children: [_jsx("i", {}), _jsx("i", {})] })] }));
}
/** Lo que la despedida se queda en pantalla: sólo el desvanecido. */
const FAREWELL_MS = FADE_MS;
/**
 * Cuánto más se queda la espera después de que lleguen los datos.
 *
 * `visible` sigue siendo cierto mientras dura el desvanecido, así que quien lo
 * consulta mantiene su pantalla de espera hasta que la mascota termina de irse.
 * Al pasar a falso, el contenido se monta por primera vez.
 *
 * ── Por qué se ajusta durante el renderizado ────────────────────────────────
 * Porque en un efecto llega tarde. En el fotograma en que `loading` pasa a
 * falso, la despedida todavía no está activada, así que `visible` vale falso y
 * la pantalla enseña el contenido entero; al fotograma siguiente el efecto lo
 * corrige y la espera vuelve. Contenido, mascota, contenido: un parpadeo de un
 * cuadro. Ajustado aquí, React vuelve a renderizar antes de pintar y ese estado
 * intermedio no llega nunca a la pantalla.
 */
export function useLoadingMascot(loading) {
    const [farewell, setFarewell] = useState(false);
    const [prevLoading, setPrevLoading] = useState(loading);
    if (prevLoading !== loading) {
        setPrevLoading(loading);
        /* Sólo se despide de lo que llegó a esperar: al pasar de esperando a listo.
           Si al montar ya no había nada pendiente, una despedida que nadie vio
           empezar es un parpadeo raro. */
        setFarewell(!loading);
    }
    useEffect(() => {
        if (loading || !farewell)
            return;
        const timer = window.setTimeout(() => setFarewell(false), FAREWELL_MS);
        return () => window.clearTimeout(timer);
    }, [loading, farewell]);
    return { visible: loading || farewell, spinning: loading };
}
//# sourceMappingURL=LoadingMascot.js.map